import { Button, ButtonVariants } from '@components/button/Button';
import { SimpleBar } from '@shared/components/simpleBar/simpleBar';
import type { RestaurantEvent } from '@shared/types/restaurants';
import cx from 'classnames';
import { useCallback, useState } from 'react';
import type {
  Control,
  FieldPath,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useFieldArray } from 'react-hook-form';
import {
  DEFAULT_EMPTY_DISCLAIMER,
  EVENT_STEPS,
  type EventDisclaimerFormItem,
  type EventFormData,
  isPersistableDisclaimerFormItem,
} from '../RestaurantEventForm';
import { DisclaimerCard } from './DisclaimerCard';
import { DisclaimerForm } from './DisclaimerForm';
import disclaimersStyles from './DisclaimersField.scss';

export type DisclaimersStepProps = {
  control: Control<EventFormData>;
  setValue: UseFormSetValue<EventFormData>;
  watch: UseFormWatch<EventFormData>;
  restaurantEvent?: RestaurantEvent | null;
};

export const DisclaimersStep = ({
  control,
  setValue,
  watch,
  restaurantEvent,
}: DisclaimersStepProps) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'disclaimers',
  });

  const [showAllAsCards, setShowAllAsCards] = useState(() =>
    Boolean(restaurantEvent?.disclaimers?.length),
  );
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [cardEditIndex, setCardEditIndex] = useState<number | null>(null);

  const disclaimersValues = watch('disclaimers') ?? [];

  const disclaimersStepLabel =
    EVENT_STEPS.find((step) => step.id === 'event-disclaimers')?.label ??
    'Event Disclaimers';

  const rowHasCardContent = (item: EventDisclaimerFormItem | undefined) =>
    isPersistableDisclaimerFormItem(item);

  const showCardForIndex = (index: number) => {
    const item = disclaimersValues[index];
    if (!rowHasCardContent(item)) return false;
    if (editingIndex === index) return false;
    return showAllAsCards;
  };

  const handleAddDisclaimer = () => {
    if (!showAllAsCards && fields.length > 0) {
      setShowAllAsCards(true);
      setCardEditIndex(null);
    }
    const nextIndex = fields.length;
    append({ ...DEFAULT_EMPTY_DISCLAIMER });
    setEditingIndex(nextIndex);
    setCardEditIndex(null);
  };

  const exitCardEditMode = useCallback(() => {
    setEditingIndex(null);
    setCardEditIndex(null);
  }, []);

  const exitNewDisclaimerToCard = useCallback(() => {
    setEditingIndex(null);
    setCardEditIndex(null);
    setShowAllAsCards(true);
  }, []);

  const handleRemoveDisclaimerAtIndex = useCallback(
    (index: number) => {
      const isLastField = fields.length <= 1;
      remove(index);
      if (editingIndex === index) setEditingIndex(null);
      if (cardEditIndex === index) setCardEditIndex(null);
      if (isLastField) {
        setShowAllAsCards(false);
        append({ ...DEFAULT_EMPTY_DISCLAIMER });
      }
    },
    [append, cardEditIndex, editingIndex, fields.length, remove],
  );

  const disclaimerCardData = (index: number) => {
    const item = disclaimersValues[index];
    return {
      title: item?.title ?? '',
      description: item?.description?.trim() ? item.description : null,
    };
  };

  return (
    <section className={disclaimersStyles.eventFormStepDisclaimers}>
      <header>
        <h2 className={disclaimersStyles.formTitle}>{disclaimersStepLabel}</h2>
        <Button
          label="Add disclaimer"
          variant={ButtonVariants.Secondary}
          onClick={handleAddDisclaimer}
          iconName="plus"
        />
      </header>
      <p
        className={cx(
          disclaimersStyles.stepSubtitle,
          disclaimersStyles.disclaimersStepSubtitleFlat,
        )}
      >
        Legal notices, policies, and other information for guests
      </p>

      <SimpleBar className={disclaimersStyles.disclaimersListScroll}>
        <div className={disclaimersStyles.disclaimersList}>
          {fields.map((field, index) =>
            showCardForIndex(index) ? (
              <div
                key={field.id}
                className={disclaimersStyles.disclaimerRowCardWrap}
              >
                <DisclaimerCard
                  disclaimer={disclaimerCardData(index)}
                  onEdit={() => {
                    setEditingIndex(index);
                    setShowAllAsCards(true);
                    setCardEditIndex(index);
                  }}
                  onDelete={() => handleRemoveDisclaimerAtIndex(index)}
                />
              </div>
            ) : (
              <div key={field.id} className={disclaimersStyles.disclaimerRow}>
                <div className={disclaimersStyles.disclaimerRowForm}>
                  <DisclaimerForm
                    control={control}
                    index={index}
                    onDoneCardEdit={
                      cardEditIndex === index ? exitCardEditMode : undefined
                    }
                    onDoneNewDisclaimer={
                      cardEditIndex !== index
                        ? exitNewDisclaimerToCard
                        : undefined
                    }
                    onClearRow={
                      fields.length === 1
                        ? () => {
                            setValue(
                              `disclaimers.${index}` as FieldPath<EventFormData>,
                              { ...DEFAULT_EMPTY_DISCLAIMER },
                              { shouldDirty: true },
                            );
                          }
                        : undefined
                    }
                    onRemove={
                      fields.length > 1
                        ? () => handleRemoveDisclaimerAtIndex(index)
                        : undefined
                    }
                    removeTooltipId={
                      fields.length > 1
                        ? `event-disclaimer-remove-${field.id}`
                        : undefined
                    }
                  />
                </div>
              </div>
            ),
          )}
        </div>
      </SimpleBar>
    </section>
  );
};
